import { Link } from 'react-router-dom';

export default function TourCard({ tour }) {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDuration = () => {
    if (!tour.startDate || !tour.endDate) return null;
    const start = new Date(tour.startDate);
    const end = new Date(tour.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days > 0 ? `${days} ${days === 1 ? 'Day' : 'Days'}` : null;
  };

  const duration = getDuration();

  return (
    <Link
      to={`/tours/${tour.id}`}
      className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full"
    >
      {/* Tour Image */}
      <div className="relative h-56 sm:h-64 overflow-hidden bg-gray-200">
        {tour.images && tour.images.length > 0 ? (
          <img
            src={tour.images[0]}
            alt={tour.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-900">
            <svg
              className="w-16 h-16 text-white opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          {/* Available Seats Badge */}
          {tour.availableSeats !== undefined && tour.availableSeats <= 5 && tour.availableSeats > 0 && (
            <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
              Only {tour.availableSeats} left!
            </div>
          )}
          {tour.availableSeats === 0 && (
            <div className="bg-gray-800 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
              Sold Out
            </div>
          )}
          <div className="flex-1"></div>
          {/* Price Badge */}
          <div className="bg-white px-3 py-1 rounded-full shadow-lg">
            <span className="text-blue-600 font-bold text-sm sm:text-base">
              {formatPrice(tour.price)}
            </span>
          </div>
        </div>
      </div>

      {/* Tour Info */}
      <div className="p-5 sm:p-6 flex flex-col flex-1">
        {/* Destination */}
        {tour.destination && (
          <div className="flex items-center text-gray-500 text-xs sm:text-sm mb-2">
            <svg
              className="w-4 h-4 mr-1 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="truncate">{tour.destination}</span>
          </div>
        )}

        {/* Title */}
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
          {tour.title}
        </h3>

        {/* Description */}
        {tour.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">
            {tour.description}
          </p>
        )}

        {/* Tour Details */}
        <div className="space-y-2 mb-4">
          {/* Dates */}
          {tour.startDate && tour.endDate && (
            <div className="flex items-center text-gray-500 text-xs sm:text-sm">
              <svg
                className="w-4 h-4 mr-2 flex-shrink-0"

                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="truncate">
                {formatDate(tour.startDate)} - {formatDate(tour.endDate)}
              </span>
            </div>
          )}

          {/* Duration */}
          {duration && (
            <div className="flex items-center text-gray-500 text-xs sm:text-sm">
              <svg
                className="w-4 h-4 mr-2 flex-shrink-0"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{duration}</span>
            </div>
          )}

          {/* Available Seats */}
          {tour.availableSeats !== undefined && tour.availableSeats > 5 && (
            <div className="flex items-center text-gray-500 text-xs sm:text-sm">
              <svg
                className="w-4 h-4 mr-2 flex-shrink-0"

                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span>{tour.availableSeats} seats available</span>
            </div>
          )}
        </div>

        {/* View Details Button */}
        <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:text-blue-700 mt-auto">
          View Details
          <svg
            className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform group-hover:animate-ping"

            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </div>
      </div>
    </Link>
  );
}